import { useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import { Upload, X } from "lucide-react";
import { FC, useEffect, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { FormattedMessage } from "react-intl";
import { UpdateOrganizationSettingsBody } from "~/api/model";
import {
  getGetOrganizationSettingsQueryKey,
  useConfirmLogoUpload,
  useGetLogoPresignedUrl,
} from "~/api/organizations/organizations";
import { Button } from "~/components/ui/button";
import { convertSvgToPng } from "~/features/chat/helpers";
import { useOrganizationParams } from "~/helpers";
import logger from "~/utils/logger";

interface SettingsBrandingProps {
  updateSettings: (data: UpdateOrganizationSettingsBody) => Promise<void>;
  logoUrl?: string | null;
  footnote?: string | null;
}

export const SettingsBranding: FC<SettingsBrandingProps> = ({
  updateSettings,
  logoUrl,
  footnote,
}) => {
  const { organizationId } = useOrganizationParams();
  const queryClient = useQueryClient();

  const presignMutation = useGetLogoPresignedUrl();
  const confirmMutation = useConfirmLogoUpload();

  const [logoPreview, setLogoPreview] = useState<string | null>(() => {
    return logoUrl || null;
  });
  const [isDragActive, setIsDragActive] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [footnoteValue, setFootnoteValue] = useState<string>(footnote || "");
  const [footnoteError, setFootnoteError] = useState<string | null>(null);

  const tempPreviewUrlRef = useRef<string | null>(null);

  useEffect(() => {
    setLogoPreview(logoUrl || null);
    if (tempPreviewUrlRef.current) {
      URL.revokeObjectURL(tempPreviewUrlRef.current);
      tempPreviewUrlRef.current = null;
    }
  }, [logoUrl]);

  useEffect(() => {
    setFootnoteValue(footnote || "");
  }, [footnote]);

  useEffect(() => {
    return () => {
      if (tempPreviewUrlRef.current) {
        URL.revokeObjectURL(tempPreviewUrlRef.current);
        tempPreviewUrlRef.current = null;
      }
    };
  }, []);

  const getExtFromName = (name: string) => {
    const match = name.match(/\.([a-zA-Z0-9]+)$/);
    return match ? match[1].toLowerCase() : "png";
  };

  const handleDrop = async (acceptedFiles: File[]) => {
    setError(null);
    if (acceptedFiles.length === 0) return;
    let file = acceptedFiles[0];

    const previousLogo = logoUrl || null;

    if (tempPreviewUrlRef.current) {
      URL.revokeObjectURL(tempPreviewUrlRef.current);
      tempPreviewUrlRef.current = null;
    }

    const objectUrl = URL.createObjectURL(file);
    tempPreviewUrlRef.current = objectUrl;
    setLogoPreview(objectUrl);

    // begin upload via presigned URL
    try {
      setIsUploading(true);

      const previousLogo = logoUrl || null;

      let ext = getExtFromName(file.name);
      let contentType =
        file.type || (ext === "svg" ? "image/svg+xml" : `image/${ext}`);
      let uploadBody: Blob | File = file;

      // Convert SVG to PNG before upload
      if (ext === "svg" || contentType.includes("svg")) {
        logger.info("Converting SVG to PNG for upload");
        const pngDataUrl = await convertSvgToPng(file);

        const response = await fetch(pngDataUrl);
        uploadBody = await response.blob();

        ext = "png";
        contentType = "image/png";
        logger.info("SVG converted to PNG successfully");
      }

      const presignResp = await presignMutation.mutateAsync({
        organizationId,
        data: { contentType: contentType as any, ext: ext as any },
      });

      const presignedUrl = presignResp?.presignedUrl;
      const s3Key = presignResp?.s3Key;

      if (!presignedUrl || !s3Key) {
        throw new Error("Failed to obtain presigned URL");
      }

      const uploadResp = await fetch(presignedUrl, {
        method: "PUT",
        body: uploadBody,
        headers: {
          "Content-Type": contentType,
        },
      });

      if (!uploadResp.ok) {
        throw new Error(`Upload failed. Please try again.`);
      }

      // confirm upload with backend which will save logoS3Key to settings
      const confirmResp = await confirmMutation.mutateAsync({
        organizationId,
        data: { s3Key },
      });
      const newLogoUrl = confirmResp?.organization?.logoUrl ?? null;

      if (tempPreviewUrlRef.current) {
        URL.revokeObjectURL(tempPreviewUrlRef.current);
        tempPreviewUrlRef.current = null;
      }

      if (newLogoUrl) {
        setLogoPreview(newLogoUrl);
      } else {
        setLogoPreview(previousLogo);
      }

      // Invalidate settings query cache to refetch latest data including logoUrl
      await queryClient.invalidateQueries({
        queryKey: getGetOrganizationSettingsQueryKey(organizationId),
      });
    } catch (err: any) {
      logger.error("Logo upload failed:", err);
      setError(err?.message || "Upload failed");
      if (tempPreviewUrlRef.current) {
        URL.revokeObjectURL(tempPreviewUrlRef.current);
        tempPreviewUrlRef.current = null;
      }
      setLogoPreview(previousLogo);
    } finally {
      setIsUploading(false);
    }
  };

  const handleRemoveLogo = async () => {
    const previousLogo = logoUrl || null;
    setError(null);
    setLogoPreview(null);
    if (tempPreviewUrlRef.current) {
      URL.revokeObjectURL(tempPreviewUrlRef.current);
      tempPreviewUrlRef.current = null;
    }

    try {
      await updateSettings({ logoS3Key: null });
    } catch (err) {
      logger.error("Logo removal failed:", err);
      setError("Failed to remove logo");
      setLogoPreview(previousLogo);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDragEnter: () => setIsDragActive(true),
    onDragLeave: () => setIsDragActive(false),
    onDrop: (acceptedFiles) => {
      setIsDragActive(false);
      handleDrop(acceptedFiles);
    },
    accept: {
      "image/png": [".png"],
      "image/svg+xml": [".svg"],
      "image/jpeg": [".jpg", ".jpeg"],
    },
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="w-full flex flex-col gap-2">
      <span className="text-sm font-medium">
        <FormattedMessage
          id="settings.branding.companyLogo"
          defaultMessage={"Company Logo"}
        />
      </span>
      <p className="text-xs text-muted-foreground">
        <FormattedMessage
          id="settings.branding.companyLogo.description"
          defaultMessage={
            "Upload your company logo. Recommended size: 300x100px, PNG or SVG format."
          }
        />
      </p>

      {logoPreview ? (
        <div className="border border-zinc-200 rounded-lg p-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="border border-zinc-200 rounded p-2 bg-white">
              <img
                src={logoPreview}
                alt="Company logo"
                className="h-16 w-auto max-w-[200px] object-contain"
              />
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleRemoveLogo}
            className="text-muted-foreground hover:text-destructive"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <div
          {...getRootProps()}
          className={clsx(
            "border-2 border-dashed rounded-lg p-8 cursor-pointer transition-all duration-200",
            {
              "border-blue-500 bg-blue-50": isDragActive,
              "border-zinc-200 hover:border-zinc-300": !isDragActive,
            }
          )}
        >
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="bg-white h-12 w-12 rounded-full border border-zinc-200 flex items-center justify-center">
              <Upload className="w-5 h-5 text-zinc-500" />
            </div>
            <div className="flex flex-col gap-1">
              <p className="text-sm font-medium text-zinc-700">
                <FormattedMessage
                  id="settings.branding.dragAndDrop"
                  defaultMessage={"Drag and drop your logo here"}
                />
              </p>
              <p className="text-xs text-muted-foreground">
                <FormattedMessage
                  id="settings.branding.orClickToUpload"
                  defaultMessage={"or click to browse"}
                />
              </p>
            </div>
            <Button
              variant="secondary"
              size="sm"
              className="bg-blue-100 text-blue-600 hover:bg-blue-200"
              type="button"
            >
              <FormattedMessage
                id="settings.branding.chooseFile"
                defaultMessage={"Choose File"}
              />
            </Button>
          </div>
          <input {...getInputProps()} className="hidden" />
        </div>
      )}
      {isUploading && (
        <p className="text-xs text-muted-foreground">Uploading...</p>
      )}
      {error && <p className="text-xs text-destructive">{error}</p>}

      <div className="mt-6 flex flex-col gap-2">
        <span className="text-sm font-medium">
          <FormattedMessage
            id="settings.branding.footnote"
            defaultMessage={"Footnote"}
          />
        </span>
        <p className="text-xs text-muted-foreground">
          <FormattedMessage
            id="settings.branding.footnote.description"
            defaultMessage={"Enter custom footnote."}
          />
        </p>
        <div className="flex flex-col gap-1">
          <input
            type="text"
            value={footnoteValue}
            onChange={(e) => {
              const value = e.target.value;
              if (value.length <= 30) {
                setFootnoteValue(value);
                setFootnoteError(null);
              } else {
                setFootnoteError("Maximum 30 characters allowed");
              }
            }}
            onBlur={async () => {
              if (footnoteValue.length <= 30) {
                try {
                  await updateSettings({
                    footnote: footnoteValue.trim() || null,
                  } as any);
                } catch (err) {
                  logger.error("Footnote update failed:", err);
                  setFootnoteError("Failed to update footnote");
                }
              }
            }}
            placeholder="Powered by Sky AI"
            className="border border-zinc-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            maxLength={30}
          />
          <div className="flex justify-between items-center">
            <span className="text-xs text-muted-foreground">
              {footnoteValue.length}/30
            </span>
            {footnoteError && (
              <span className="text-xs text-destructive">{footnoteError}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
